import { useState } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { toast } from "sonner";
import { Lock, Mail, Key, CheckCircle, ShieldCheck } from "lucide-react";
import { API_URL } from "../config/api";

export default function EmergencyResetPage() {
  const [email, setEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [puk, setPuk] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleReset = async (e) => {
    e.preventDefault();
    
    if (!email || !newPassword || !puk) {
      toast.error("Please fill all fields");
      return;
    }

    if (newPassword.length < 4) {
      toast.error("Password must be at least 4 characters");
      return;
    }

    if (puk.length !== 8) {
      toast.error("PUK must be 8 characters");
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/auth/puk-reset`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          puk: puk.toUpperCase(),
          email: email,
          new_password: newPassword
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSuccess(true);
        toast.success(`Password reset successful for ${email}`);
      } else {
        toast.error(data.detail || "Failed to reset password. Check your PUK code.");
      }
    } catch (error) {
      toast.error("Connection error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-green-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 text-center">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-green-700 mb-2">Password Reset Successful!</h2>
            <p className="text-gray-600 mb-6">
              You can now log in with your new password.
            </p>
            <Button 
              onClick={() => window.location.href = "/login"}
              className="w-full"
            >
              Go to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <div className="p-3 bg-blue-100 rounded-full">
              <ShieldCheck className="w-8 h-8 text-blue-600" />
            </div>
          </div>
          <CardTitle className="text-xl text-slate-800">Password Recovery with PUK</CardTitle>
          <CardDescription>
            Use your Personal Unlock Key (PUK) to reset your password.
            <br />
            <span className="text-xs text-gray-500">You received your PUK when you created your account.</span>
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleReset} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="flex items-center gap-2">
                <Mail className="w-4 h-4" />
                Your Email
              </Label>
              <Input
                id="email"
                type="email"
                placeholder="your.email@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="puk" className="flex items-center gap-2">
                <Key className="w-4 h-4" />
                PUK Code
              </Label>
              <Input
                id="puk"
                type="text"
                placeholder="8-character code (e.g., AB12CD34)"
                value={puk}
                onChange={(e) => setPuk(e.target.value.toUpperCase())}
                maxLength={8}
                className="font-mono tracking-widest text-center uppercase"
                required
              />
              <p className="text-xs text-gray-500">
                Don't have your PUK? Contact your administrator.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="flex items-center gap-2">
                <Lock className="w-4 h-4" />
                New Password
              </Label>
              <Input
                id="password"
                type="password"
                placeholder="Enter new password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={4}
              />
            </div>

            <Button 
              type="submit" 
              className="w-full"
              disabled={loading}
            >
              {loading ? "Resetting..." : "Reset Password"}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <a href="/login" className="text-sm text-blue-600 hover:underline">
              ← Back to Login
            </a>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
